import { createStaffInputSchema, type CreateStaffInput, type StaffRole } from '@mybusinessmate/schema';

/** Staff addresses all live on one domain (Figma "Add staff": the field shows a fixed "@mybusinessmate.ai" suffix). */
export const STAFF_EMAIL_DOMAIN = 'mybusinessmate.ai';

export interface StaffFormValues {
  name: string;
  /** The part before the @. */
  emailLocal: string;
  role: StaffRole;
  department: string;
  title: string;
  skills: string[];
  maxActiveTasks: number;
}

export const emptyStaffForm: StaffFormValues = {
  name: '',
  emailLocal: '',
  role: 'specialist',
  department: '',
  title: '',
  skills: [],
  maxActiveTasks: 5,
};

export const ROLE_OPTIONS: { value: StaffRole; label: string; help: string }[] = [
  {
    value: 'specialist',
    label: 'Specialist',
    help: 'Specialists only see tasks assigned to them, in their workbench.',
  },
  {
    value: 'lead_specialist',
    label: 'Lead specialist',
    help: 'Leads review, approve and publish drafts for the tasks they lead.',
  },
  { value: 'account_manager', label: 'Account manager', help: 'Account managers run the clients assigned to them.' },
  { value: 'admin', label: 'Admin', help: 'Admins can see and manage everything.' },
];

export type StaffFormErrors = Partial<Record<keyof StaffFormValues, string>>;

export type StaffFormResult = { ok: true; body: CreateStaffInput } | { ok: false; errors: StaffFormErrors };

/** Maps the API body's `email` problems back onto the visible "local part" field. */
const FIELD_FOR_PATH: Record<string, keyof StaffFormValues> = {
  email: 'emailLocal',
  name: 'name',
  staffRole: 'role',
  department: 'department',
  title: 'title',
  skills: 'skills',
  maxActiveTasks: 'maxActiveTasks',
};

/** Validates with the same schema the API uses, so the form can never disagree with the server about what is valid. */
export function toStaffRequest(values: StaffFormValues, domain: string = STAFF_EMAIL_DOMAIN): StaffFormResult {
  const body: CreateStaffInput = {
    name: values.name.trim(),
    email: `${values.emailLocal.trim().toLowerCase()}@${domain}`,
    staffRole: values.role,
    maxActiveTasks: values.maxActiveTasks,
    ...(values.department.trim() ? { department: values.department.trim() } : {}),
    ...(values.title.trim() ? { title: values.title.trim() } : {}),
    ...(values.skills.length ? { skills: values.skills } : {}),
  };

  const parsed = createStaffInputSchema.safeParse(body);
  if (parsed.success) return { ok: true, body };

  const errors: StaffFormErrors = {};
  for (const issue of parsed.error.issues) {
    const field = FIELD_FOR_PATH[String(issue.path[0])];
    if (field) errors[field] ??= field === 'emailLocal' ? 'Enter a valid work email' : issue.message;
  }
  return { ok: false, errors };
}
